import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import PageBreadcrumb from '../../../../../../Components/Common/PageBreadcrumb';
import classnames from 'classnames';
import notify from '../../../../../../utils/notify';
import useProcurementDetail from '../../../../../../shared/offer-domain/procurement/useProcurementDetail';
import useSubmittedBidder from '../../../../hooks/useSubmittedBidder';
import useClarificationBidders from '../../../../hooks/useClarificationBidders';
import { RESPOND_LIST_PAGE_SIZE } from '../../../../../../shared/offer-domain/respond/listParams';
import useClarificationProgressActions from '../../../../hooks/useClarificationProgressActions';
import {
  canMutateEvaluationOperationalData,
  displayValue,
} from '../../../../utils/tenderEvaluationHelpers';
import {
  CLARIFICATION_STATUS,
  getClarificationStatusBadge,
  isTeClarificationEditable,
  isTeClarificationLocked,
} from '../../../../../../shared/offer-domain/clarification';
import {
  hasProposalValidityCounter,
  withBidderCounter,
} from '../../../../../../shared/offer-domain/counter';
import {
  DateCountdown,
  SubmitConfirmModal,
} from '../../../../../../shared/offer-domain/ui';
import {
  getVisibleProcurementClauses,
  useMarkClarificationRespondsAsRead,
} from '../../../../../../shared/offer-domain/respond';
import OfferingTab from './tabs/OfferingTab';
import DocumentAttachmentTab from './tabs/DocumentAttachmentTab';
import ProcurementGtcTab from '../../../../../../shared/offer-domain/procurement/ProcurementGtcTab';
import '../../../../TenderEvaluation.css';
import ActionButton from '../../../../../../Components/Common/ActionButton';
import { DetailBackButton } from '../../../../../../Components/Common/Detail';
import { SkeletonCard } from '../../../../../../Components/Common/Skeleton';
import StatusBadge, {
  colorToTone,
} from '../../../../../../Components/Common/StatusBadge';

const MENU_TABS = [
  {
    id: 'offering',
    label: 'Offering',
    icon: 'ri-store-2-line',
  },
  {
    id: 'document-attachment',
    label: 'Document Attachment',
    icon: 'ri-file-text-line',
  },
  // Read-only, so it is not part of pendingBySourceRef.
  {
    id: 'gtc-amend',
    label: 'GTC Amend',
    icon: 'ri-scales-3-line',
  },
];

const CONFIRM_COPY = {
  closeClarification: {
    title: 'Close this clarification?',
    message:
      'Tender Evaluation and Improvement Clarification will be marked as Closing and no further clarification can be sent for this offer. This cannot be undone.',
  },
  sendClarification: {
    title: 'Send clarification to the registered supplier?',
    message:
      'The clarification is sent right away and the status moves to Waiting Response from Registered Supplier. It cannot be recalled.',
  },
};

const ClarificationDetail = ({
  procurementId: procurementIdProp,
  offerId: offerIdProp,
  procurement: procurementProp,
  isLoadingProcurement,
} = {}) => {
  const params = useParams();
  const procurementId = procurementIdProp ?? params.id;
  // Holds procurementMitraId; IdOffer is not exposed in Clarification.
  const offerId = offerIdProp ?? params.procurementMitraId;
  const navigate = useNavigate();
  const hasSharedProcurement = isLoadingProcurement !== undefined;
  const { procurement: fetchedProcurement, isLoading: isLoadingFetched } =
    useProcurementDetail(procurementId, { enabled: !hasSharedProcurement });
  const procurement = hasSharedProcurement
    ? procurementProp
    : fetchedProcurement;
  const isLoading = hasSharedProcurement
    ? isLoadingProcurement
    : isLoadingFetched;
  const { items: submittedBidders, refresh } = useSubmittedBidder(
    procurementId,
    { pageSize: RESPOND_LIST_PAGE_SIZE }
  );
  const {
    items: clarificationBidders,
    isLoading: isLoadingClarificationBidders,
  } = useClarificationBidders(procurementId);
  const matchedBidder = submittedBidders.find(
    bidder =>
      String(bidder.idOffer ?? '') === String(offerId ?? '') ||
      String(bidder.id ?? '') === String(offerId ?? '')
  );
  const procurementMitraId = matchedBidder?.id ?? null;
  const validityCountdown = useMemo(() => {
    const merged =
      withBidderCounter(procurement, matchedBidder) || procurement || {};
    const hasCounter = hasProposalValidityCounter(
      merged?.proposalValidityPeriodMaxCounter
    );
    return {
      deadline: hasCounter
        ? merged.proposalValidityPeriodMaxCounter
        : merged?.proposalValidityPeriodMax,
      label: hasCounter ? 'Validity date' : 'Validity Procurement',
      openLabel: hasCounter
        ? 'Validity ends in'
        : 'Validity Procurement ends in',
      closedLabel: hasCounter ? 'Validity ended' : 'Validity Procurement ended',
    };
  }, [procurement, matchedBidder]);
  const [gtcAmendCount, setGtcAmendCount] = useState(null);
  const gtcAmendTotal =
    gtcAmendCount ?? matchedBidder?.gtcAmendedClauseCount ?? 0;
  const [activeTab, setActiveTab] = useState('offering');
  const [confirmAction, setConfirmAction] = useState(null);
  const [localStatus, setLocalStatus] = useState(null);
  const [highlightArmed, setHighlightArmed] = useState(false);
  const [focusRespondId, setFocusRespondId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const pendingBySourceRef = useRef({ offering: [], document: [] });
  const hasRespondBySourceRef = useRef({ offering: false, document: false });
  const [pendingReady, setPendingReady] = useState({
    offering: false,
    document: false,
  });
  const [livePendingTargets, setLivePendingTargets] = useState([]);
  const [hasClarificationRespond, setHasClarificationRespond] = useState(false);
  const { savingAction, isSaving, sendClarification, closeClarification } =
    useClarificationProgressActions({
      procurementMitraId: matchedBidder?.id ?? offerId,
      procurementId,
    });

  useEffect(() => () => toast.dismiss(), []);

  useEffect(() => {
    setHighlightArmed(false);
    setFocusRespondId(null);
    setFocusToken(0);
    pendingBySourceRef.current = { offering: [], document: [] };
    hasRespondBySourceRef.current = { offering: false, document: false };
    setPendingReady({ offering: false, document: false });
    setLivePendingTargets([]);
    setHasClarificationRespond(false);
    setGtcAmendCount(null);
  }, [offerId]);

  useEffect(() => {
    if (!procurementId) return;
    refresh();
  }, [procurementId, offerId, refresh]);

  const handlePendingFrom = useCallback((source, targets, meta = {}) => {
    pendingBySourceRef.current[source] = targets ?? [];
    if (typeof meta.hasClarificationRespond === 'boolean') {
      hasRespondBySourceRef.current[source] = meta.hasClarificationRespond;
      const nextHasRespond =
        Boolean(hasRespondBySourceRef.current.offering) ||
        Boolean(hasRespondBySourceRef.current.document);
      setHasClarificationRespond(prev =>
        prev === nextHasRespond ? prev : nextHasRespond
      );
    }
    setPendingReady(prev =>
      prev[source] ? prev : { ...prev, [source]: true }
    );
    const next = [
      ...(pendingBySourceRef.current.offering ?? []),
      ...(pendingBySourceRef.current.document ?? []),
    ];
    setLivePendingTargets(prev => {
      if (
        prev.length === next.length &&
        prev.every((item, index) => item.id === next[index]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleOfferingPending = useCallback(
    (targets, meta) => handlePendingFrom('offering', targets, meta),
    [handlePendingFrom]
  );
  const handleDocumentPending = useCallback(
    (targets, meta) => handlePendingFrom('document', targets, meta),
    [handlePendingFrom]
  );

  const isPendingReady = pendingReady.offering && pendingReady.document;

  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );

  useMarkClarificationRespondsAsRead({
    procurementId,
    procurementMitraId,
    clauseDetailIds,
    isEvaluatorView: true,
    enabled: Boolean(procurementId && procurementMitraId),
  });

  const statusClarification =
    localStatus !== null ? localStatus : matchedBidder?.statusClarification;
  const statusBadge = getClarificationStatusBadge(
    statusClarification,
    matchedBidder?.statusClarificationLabel
  );
  const canMutateEvaluation = canMutateEvaluationOperationalData(procurement);
  const isEditable =
    canMutateEvaluation && isTeClarificationEditable(statusClarification);
  const isRespondClosed =
    !canMutateEvaluation || isTeClarificationLocked(statusClarification);

  useEffect(() => {
    if (!focusRespondId) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`ic-respond-${focusRespondId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusRespondId, activeTab, focusToken]);

  const sendDisabled = isSaving || !isPendingReady || !hasClarificationRespond;
  const showSendRespondHint = isPendingReady && !hasClarificationRespond;

  const handleOpenSend = () => {
    if (!isEditable) return;
    if (!isPendingReady) return;
    if (livePendingTargets.length) {
      const first = livePendingTargets[0];
      setHighlightArmed(true);
      setActiveTab(first.tab);
      setFocusRespondId(first.id);
      setFocusToken(token => token + 1);
      notify.warning(
        'Last counter responses must be agreed or responded to before sending clarification.'
      );
      return;
    }
    if (!hasClarificationRespond) return;
    setHighlightArmed(false);
    setFocusRespondId(null);
    setConfirmAction('sendClarification');
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    let success = false;
    if (action === 'closeClarification') {
      success = await closeClarification();
      if (success) {
        setLocalStatus(CLARIFICATION_STATUS.CLOSING);
      }
    } else if (action === 'sendClarification') {
      success = await sendClarification(statusClarification);
      if (success) {
        setLocalStatus(CLARIFICATION_STATUS.WAITING_DMUT);
      }
    }
    if (success) {
      setConfirmAction(null);
      await refresh();
      setLocalStatus(null);
    }
  };

  const backUrl = `/apps-tenderevaluation/dtl/${procurementId}`;
  const procurementNumber = displayValue(procurement?.procurementNumber);
  const mitraRow = clarificationBidders.find(
    b => String(b.id) === String(offerId)
  );
  const offerDisplay =
    isLoadingClarificationBidders && !mitraRow
      ? '…'
      : displayValue(mitraRow?.companyName);

  document.title = 'Clarification Detail';

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                <div className='border-bottom border-bottom-dashed'>
                  <Row className='mt-n2 mb-1 mb-sm-0'>
                    <div className='col-sm order-3 order-sm-2'>
                      <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                        <h5 className='card-title mb-0 flex-grow-1'>
                          <PageBreadcrumb
                            listClassName='p-3 py-2 bg-light'
                            fallback={{
                              label: 'Tender Evaluation',
                              to: '/apps-tenderevaluation/view',
                            }}
                            parents={[
                              {
                                label: `Detail #${procurementNumber}`,
                                to: backUrl,
                              },
                              { label: 'Clarification' },
                            ]}
                            current={offerDisplay}
                          />
                        </h5>
                      </div>
                    </div>
                  </Row>

                  <Row className='mb-4'>
                    <Col xs={12}>
                      <Row className='align-items-center g-3'>
                        <Col xs={12} lg>
                          <div>
                            <div className='dt-header__top'>
                              <DetailBackButton
                                label='Back'
                                onClick={() => navigate(backUrl)}
                              />
                              <StatusBadge
                                tone={colorToTone(statusBadge.color)}
                                label={`Status: ${statusBadge.label}`}
                              />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
                                <h4 className='mb-0 fw-bold text-dark text-break'>
                                  Clarification — Offer {offerDisplay}
                                </h4>
                              </div>
                              <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                                <i className='ri-hashtag'></i>
                                <span>Procurement ID: {procurementNumber}</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} lg='auto'>
                          <DateCountdown
                            label={validityCountdown.label}
                            deadline={validityCountdown.deadline}
                            openLabel={validityCountdown.openLabel}
                            closedLabel={validityCountdown.closedLabel}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>

                <div className='message-list-content mx-n4 px-4 message-list-scroll hy-detail-scroll'>
                  <Card>
                    <CardBody className='checkout-tab'>
                      <Nav
                        tabs
                        className='nav-tabs-custom nav-primary mt-1 mb-3'
                      >
                        {MENU_TABS.map(tab => (
                          <NavItem key={tab.id}>
                            <NavLink
                              tag='button'
                              type='button'
                              className={classnames('cursor-pointer', {
                                active: activeTab === tab.id,
                              })}
                              onClick={() => setActiveTab(tab.id)}
                            >
                              <i
                                className={`${tab.icon} me-1 align-bottom`}
                              ></i>
                              <span className='fw-semibold'>{tab.label}</span>
                              {tab.id === 'gtc-amend' && (
                                <span
                                  className={`badge rounded-pill ms-1 ${
                                    gtcAmendTotal > 0
                                      ? 'bg-primary'
                                      : 'bg-light text-muted'
                                  }`}
                                >
                                  {gtcAmendTotal}
                                </span>
                              )}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId='offering'>
                          {isLoading && !procurement ? (
                            <>
                              <SkeletonCard lines={3} className='mb-3' />
                              <SkeletonCard lines={4} />
                            </>
                          ) : (
                            <OfferingTab
                              procurement={procurement}
                              procurementId={procurementId}
                              offerId={offerId}
                              procurementMitraId={procurementMitraId}
                              isRespondClosed={isRespondClosed}
                              highlightArmed={highlightArmed}
                              onPendingRespondIds={handleOfferingPending}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId='document-attachment'>
                          <DocumentAttachmentTab
                            procurementId={procurementId}
                            offerId={offerId}
                            procurementMitraId={procurementMitraId}
                            isRespondClosed={isRespondClosed}
                            highlightArmed={highlightArmed}
                            onPendingRespondIds={handleDocumentPending}
                          />
                        </TabPane>
                        <TabPane tabId='gtc-amend'>
                          {activeTab === 'gtc-amend' && procurementMitraId && (
                            <ProcurementGtcTab
                              procurementId={procurementId}
                              procurementMitraId={String(procurementMitraId)}
                              showAmend
                              onLoaded={data =>
                                setGtcAmendCount(
                                  Number(data?.amendedClauseCount) || 0
                                )
                              }
                            />
                          )}
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>

                  {isEditable && (
                    <div className='req-actionbar flex-wrap align-items-center justify-content-between gap-3'>
                      <div className='flex-grow-1 me-2' style={{ minWidth: 0 }}>
                        {showSendRespondHint ? (
                          <div
                            className='alert alert-warning py-2 px-3 mb-0 small text-break'
                            role='status'
                          >
                            Add an evaluator respond to enable Send. Or use
                            Closing Clarification if none is needed.
                          </div>
                        ) : null}
                      </div>
                      <div className='d-inline-flex align-items-center gap-2 flex-wrap flex-shrink-0'>
                        <ActionButton
                          variant='destructive'
                          confirm={false}
                          icon='ri-checkbox-circle-line'
                          disabled={isSaving}
                          loading={savingAction === 'closeClarification'}
                          onClick={() => setConfirmAction('closeClarification')}
                        >
                          Closing Clarification
                        </ActionButton>
                        <ActionButton
                          variant='primary'
                          icon='ri-send-plane-line'
                          disabled={sendDisabled}
                          loading={savingAction === 'sendClarification'}
                          onClick={handleOpenSend}
                        >
                          Send Clarification
                        </ActionButton>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <SubmitConfirmModal
        isOpen={confirmAction === 'closeClarification'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={CONFIRM_COPY.closeClarification.title}
        message={CONFIRM_COPY.closeClarification.message}
        confirmLabel='Close clarification'
        isSubmitting={savingAction === 'closeClarification'}
      />
      <SubmitConfirmModal
        isOpen={confirmAction === 'sendClarification'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={CONFIRM_COPY.sendClarification.title}
        message={CONFIRM_COPY.sendClarification.message}
        confirmLabel='Send clarification'
        isSubmitting={savingAction === 'sendClarification'}
      />
    </React.Fragment>
  );
};

export default ClarificationDetail;
